import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const CompositeFillings = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Composite Fillings Fort Worth TX | Tooth-Colored Fillings | Fort Worth Dental</title>
        <meta name="description" content="Natural-looking composite fillings in Fort Worth TX. Tooth-colored dental fillings that blend seamlessly with your smile. Mercury-free option. Call (817) 985-3337." />
        <meta name="keywords" content="composite fillings fort worth, tooth colored fillings fort worth tx, white fillings, mercury free fillings, dental fillings" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-1.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Composite Fillings in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Natural-Looking Tooth Restoration</p>
            <p className="text-sm mt-4 opacity-90">Tooth-Colored Fillings | Mercury-Free | Natural Appearance</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Are Composite Fillings */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/composite-new-1.jpg" 
                  alt="Tooth-colored composite fillings Fort Worth TX - Natural looking dental restoration to repair cavities" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Are Composite Fillings?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  A modern advance in dental technology has developed a far superior plan for filling cavities. Composite fillings use a mixture of glass and tooth-colored plastic to restore decayed teeth to a healthy condition.
                </p>
              </div>
            </div>
          </section>

          {/* How Is A Composite Filling Placed */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  How Is A Composite Filling Placed?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  The process is quite similar to traditional silver fillings. After numbing the tooth for the comfort of the patient, the composite is applied in layers. A special light is used which hardens the material after the application of each layer. After applying the layers, the composite filling is molded to fit the tooth. The dentist then polishes the filling to eliminate stains and early wear.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/composite-new-2.jpg" 
                  alt="Composite filling procedure at Fort Worth Dental - Dentist applying tooth-colored resin in layers with curing light" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Advances in Composite Technology */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/composite-new-1.jpg" 
                  alt="Advanced composite filling technology - Modern materials that seamlessly match natural tooth structure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-1.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Advances in Composite Technology</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  For many years, the standard option for restoring damaged teeth and filling cavities was silver fillings. The main problem was that these silver fillings were well known for cracking. Advanced technology produced a product that was definitely superior to these silver fillings. These modern composite fillings integrate with your natural teeth far better than they ever could before. This also made cracked fillings far simpler to repair.
                </p>
              </div>
            </div>
          </section>

          {/* Weakness In Silver Fillings */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Weakness In Silver Fillings</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Researchers noticed several weaknesses in silver fillings:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                <span className="text-lg">As the temperature regularly contracts and expands the silver fillings, they have a strong tendency to crack.</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                <span className="text-lg">Silver fillings seem to wear down around the edges, which also leads to weakness and breaking.</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                <span className="text-lg">When the silver breaks, the tooth is exposed to more bacteria and additional cavities.</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                <span className="text-lg">Since silver fillings contain as much as 50% mercury, your teeth and gums will get stained if the filling corrodes and leaks.</span>
              </li>
            </ul>
          </section>

          {/* Advantages In Composite Fillings */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Advantages In Composite Fillings</h2>
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                    <span className="text-lg">Composite fillings look far more natural and are able to be matched to the color of the patient's remaining original teeth.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                    <span className="text-lg">Composite fillings are stronger and longer lasting.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-2xl flex-shrink-0">◆</span>
                    <span className="text-lg">If you have been treated with silver fillings in the past, your dentist can avoid potential dental problems by replacing the silver fillings with composite fillings.</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/composite-new-2.jpg" 
                  alt="Composite Filling Advantages" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Additional Benefits */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Additional Benefits of Composite Fillings</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>No mercury or other metals - completely biocompatible</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Requires less removal of healthy tooth structure</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Bonds directly to tooth enamel for added strength</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Less sensitivity to temperature changes</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Can be repaired if damaged</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Suitable for front and back teeth</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Composite Fillings?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              We use only the highest quality composite materials for natural-looking, long-lasting results.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Mercury-Free</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Composite Filling Appointment</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for natural-looking, mercury-free fillings in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Composite Filling Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CompositeFillings;